import React from 'react'
import {Box, Typography} from '@mui/material'
import {kpiCardSx, kpiValueSx, kpiLabelSx} from '../../../styles/patterns'
import {SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {PRIMARY_BLUE, WARNING, SUCCESS, withOpacity} from '../../../styles/foundation/colors'

/**
 * The five overview counters above the queue.
 *
 * Every card is a destination, not decoration: each names a set of shipments
 * and opens the tab that holds them. Three are deliberately scoped to match
 * their destination tab's own count exactly — at risk = Action Required,
 * disputed = In Dispute, resolved = Resolved — so a merchant can read the strip
 * against the tabs and see the same number twice, rather than two figures that
 * nearly agree.
 */
export default function KpiStrip({
  totals, onRiskClick, onExpiringClick, onDisputedClick, onResolvedClick, onAvgClick,
}) {
  const cards = [
    {
      key: 'risk',
      label: 'Shipments at risk',
      value: totals.atRiskCount,
      color: WARNING,
      onClick: onRiskClick,
      hint: 'still waiting on your decision',
    },
    {
      key: 'exp',
      label: 'Auto-accept in 48h',
      value: totals.expiring48h,
      color: WARNING,
      onClick: onExpiringClick,
      hint: 'the courier weight is accepted for you if the clock runs out',
    },
    // Disputed sits before resolved: it is the earlier step in the same
    // lifecycle, so the row reads left to right in the order work happens.
    {
      key: 'disputed',
      label: 'Shipments disputed',
      value: totals.disputedCount,
      color: PRIMARY_BLUE,
      onClick: onDisputedClick,
      hint: 'filed, awaiting the courier or Prozo',
    },
    {
      // "Disputes resolved", not "Shipments recovered" — and it reads
      // resolvedCount, every row that reached an outcome, not the won subset a
      // card labelled "recovered" was counting. A card that says resolved has
      // to count resolutions, or it reports 20 beside a Resolved tab reading 43.
      key: 'resolved',
      label: 'Disputes resolved',
      value: totals.resolvedCount,
      color: SUCCESS,
      onClick: onResolvedClick,
      hint: 'reached an outcome, won or lost',
    },
    {
      key: 'avg',
      label: 'Avg Resolution',
      // The server sends null when nothing in the window has been resolved yet — a merchant's first
      // weeks, or a date range with no closures — which printed "null days".
      value: totals.avgResolutionDays == null ? '—' : totals.avgResolutionDays + ' days',
      color: PRIMARY_BLUE,
      onClick: onAvgClick,
      hint: 'average days from raised to resolved',
    },
  ]

  return (
    <Box sx={{mb: 1.5}}>
      {/* Equal-height, compact cards — a CSS grid (not a flex-wrap row) so all
          five are exactly the same height regardless of content, and a
          five-column row at wide widths collapsing sensibly below rather than
          wrapping raggedly. 18px values and 1.25 padding: the strip is context
          above the queue, so it is the first place to give height back. */}
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)'}, gap: 1}}>
        {cards.map((c) => {
          const clickable = typeof c.onClick === 'function'
          return (
            <Box
              key={c.key}
              // A real <button> when it acts like one. These were Boxes carrying
              // an onClick: no role, no keyboard access, no focus ring and no
              // accessible name, so the only card that did anything was
              // mouse-only. Making all five clickable would have multiplied that
              // defect by five. `type='button'` matters — a bare button inside a
              // form submits it.
              component={clickable ? 'button' : 'div'}
              type={clickable ? 'button' : undefined}
              onClick={c.onClick}
              // The visible label is "Shipments at risk"; on its own that does
              // not say what clicking does or what the number means.
              aria-label={clickable ? `${c.value} ${c.label} — ${c.hint}` : undefined}
              sx={{
                ...kpiCardSx(c.color),
                p: 1.25,
                height: 'auto',
                width: '100%',
                textAlign: 'left',
                font: 'inherit',
                cursor: clickable ? 'pointer' : 'default',
                ...(clickable && {
                  '&:hover': {bgcolor: withOpacity(c.color, 0.06)},
                  // Focus has to be VISIBLE, not merely present: these sit on
                  // white and the browser default ring is easy to lose against
                  // the card's own coloured left accent.
                  '&:focus-visible': {outline: `2px solid ${c.color}`, outlineOffset: '2px'},
                }),
              }}
            >
              <Typography sx={{...kpiValueSx(c.color), fontSize: '18px', lineHeight: 1.25}}>{c.value}</Typography>
              <Typography sx={{...kpiLabelSx, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, mb: 0}}>
                {c.label}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
